import 'array-flat-polyfill';

import { configAnalytics } from '@qbe/qal';
import { config, localSpaId } from 'config';
import React from 'react';
import { preload } from 'react-dom';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { BrowserRouter as Router } from 'react-router-dom';
import smoothscroll from 'smoothscroll-polyfill';

import App from './App';
import { imgMap } from './components-ds/Icon/Icon';
import { setupMocks } from './mocks/setup-mocks';
import { store } from './store';

// Static analytics configuration
const analyticsConfig = {
  appEnv: process.env.NODE_ENV,
  appName: window.qbeTrack?.meta?.appName || 'DigiSME',
  appPartner: 'QBE Direct',
  appProduct: window.qbeTrack?.meta?.appProduct || 'SME',
  appType: 'QuoteBuy',
  initialData: {},
  isAuthenticated: false,
  basePagePath: '/au/quote/business',
  // eslint-disable-next-line no-undef
  appVersion: _SME_.VERSION,
  shouldReset: false,
  enableAdsEngagementTracking: false,
  sendQALErrorsToDynatrace: true,
  sendPageViewToDynatrace: true,
  sendQbeTrackMetaKeysToDynatrace: true,
  sendDataLayerKeysToDynatrace: ['policyId', 'quoteNumber', 'paymentType'],
  bypassPiiCheckKeys: ['policyStart'],
  enableQalMfe: true,
  enableDynatraceMfe: true,
  qalMfeConfig: {
    mfeType: 'shell' as const,
    mfeOrigins: [config.digitalPaymentUrl, config.velocityUrl].filter(
      (origin): origin is string => !!origin
    ),
    mfeContext: {
      qbeTrack: {
        meta: window.qbeTrack?.meta || {},
      },
    },
  },
};

const render = () => {
  const domRoot = document.getElementById('root')!;
  const spaId =
    process.env.NODE_ENV === 'development'
      ? localSpaId
      : domRoot?.getAttribute('data-spaid') || undefined;
  const reactRoot = createRoot(domRoot);

  // Initalise analytics configuration
  configAnalytics(analyticsConfig);

  // Preload icons used in the app
  Object.entries(imgMap).forEach(([_name, img]) => {
    preload(img, { as: 'image' });
  });

  const app = (
    <React.StrictMode>
      <Provider store={store}>
        <Router basename={config.baseUrl}>
          <App spaId={spaId} />
        </Router>
      </Provider>
    </React.StrictMode>
  );

  reactRoot.render(app);
};

// Vite requires explicit "process.env.NODE_ENV === 'development'" for code removal
if (
  (process.env.NODE_ENV === 'development' && process.env.REACT_APP_USE_MOCK_API) ||
  (process.env.NODE_ENV as string) === 'test'
) {
  setupMocks().then(() => {
    render();
  });
} else {
  render();
}

smoothscroll.polyfill();
